import { useQuery } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';

export interface MilestoneReward {
  id: string;
  milestone_type: string;
  threshold: number;
  reward_credits: number;
  reward_description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PractitionerMilestone {
  id: string;
  practitioner_id: string;
  milestone_id: string;
  achieved_at: string;
  credits_awarded: number;
  created_at: string;
  milestone_rewards?: MilestoneReward;
}

export const usePractitionerMilestones = (practitionerId?: string) => {
  const { user } = useAuth();
  const targetId = practitionerId || user?.id;

  const {
    data: milestoneRewards = [],
    isLoading: rewardsLoading,
    error: rewardsError
  } = useQuery({
    queryKey: ['milestone-rewards'],
    queryFn: async () => {
      const { data, error } = await backendClient
        .from('milestone_rewards')
        .select('*')
        .eq('is_active', true)
        .order('threshold', { ascending: true });

      if (error) throw error;
      return data as MilestoneReward[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const {
    data: achievedMilestones = [],
    isLoading: achievedLoading,
    error: achievedError
  } = useQuery({
    queryKey: ['practitioner-milestones', targetId],
    queryFn: async () => {
      if (!targetId) return [];
      
      const { data, error } = await backendClient
        .from('practitioner_milestones')
        .select(`
          *,
          milestone_rewards (*)
        `)
        .eq('practitioner_id', targetId)
        .order('achieved_at', { ascending: false });

      if (error) throw error;
      return data as PractitionerMilestone[];
    },
    enabled: !!targetId,
    staleTime: 60 * 1000, // 1 minute
  });

  const {
    data: clientCount = 0,
    isLoading: clientCountLoading
  } = useQuery({
    queryKey: ['active-client-count', targetId],
    queryFn: async () => {
      if (!targetId) return 0;
      
      const { data, error } = await backendClient
        .from('practitioner_clients')
        .select('id', { count: 'exact' })
        .eq('practitioner_id', targetId)
        .eq('is_active', true);

      if (error) throw error;
      return data?.length || 0;
    },
    enabled: !!targetId,
    staleTime: 60 * 1000, // 1 minute
  });

  // Calculate progress for each milestone
  const milestoneProgress = milestoneRewards.map(reward => {
    const isAchieved = achievedMilestones.some(
      achieved => achieved.milestone_id === reward.id
    );
    
    let currentProgress = 0;
    if (reward.milestone_type === 'client_count') {
      currentProgress = clientCount;
    }
    
    const progressPercentage = Math.min((currentProgress / reward.threshold) * 100, 100);
    
    return {
      ...reward,
      isAchieved,
      currentProgress,
      progressPercentage,
      creditsEarned: isAchieved ? reward.reward_credits : 0
    };
  });

  const totalCreditsEarned = achievedMilestones.reduce(
    (total, milestone) => total + milestone.credits_awarded, 
    0
  );

  return {
    milestoneRewards,
    achievedMilestones,
    milestoneProgress,
    clientCount,
    totalCreditsEarned,
    isLoading: rewardsLoading || achievedLoading || clientCountLoading,
    error: rewardsError || achievedError,
  };
};
